import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Adds a line to the cart over AJAX and announces it with the theme's standard
 * CartLinesUpdateEvent, so the header bubble, cart drawer and the "Added to My
 * Bag" modal all react the same way whichever button triggered it. The
 * resolved detail carries the full cart `items`, which the modal reads to show
 * the product that was just added.
 *
 * @param {HTMLElement} source - Element the event is dispatched from (it bubbles).
 * @param {FormData} formData - Must contain `id`; `quantity` defaults to 1.
 * @param {string} context
 * @returns {Promise<boolean>} true when the item was added.
 */
export function addToCart(source, formData, context) {
  const variantId = String(formData.get('id') || '');
  const quantity = Number(formData.get('quantity')) || 1;
  if (!formData.get('quantity')) formData.set('quantity', String(quantity));

  const deferredEventPromise = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context,
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferredEventPromise.promise,
    })
  );

  return fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body: formData }))
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        source.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        deferredEventPromise.reject(new Error(response.message || 'Add to cart failed'));
        return false;
      }

      return fetch(`${Theme.routes.cart_url}.js`)
        .then((res) => res.json())
        .then((cart) => {
          deferredEventPromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { source: 'brickline-add-to-cart', itemCount: quantity, items: cart.items, didError: false },
          });
          return true;
        });
    })
    .catch((error) => {
      deferredEventPromise.reject(error);
      return false;
    });
}

/**
 * Single-variant "Add to Bag" pill used by the Brickline product cards
 * (product rail, collection grid, cart upsells). Adds one unit of
 * `data-variant-id` and flashes an "Added" state.
 */
export class AddToCartPill extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    event.preventDefault();
    if (this.button?.disabled) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');

    if (this.button) {
      this.button.disabled = true;
      this.button.classList.add('is-loading');
    }

    addToCart(this, formData, this.dataset.context || 'product-card')
      .then((added) => {
        if (!added) return;
        this.classList.add('is-added');
        window.setTimeout(() => this.classList.remove('is-added'), 1800);
      })
      .finally(() => {
        if (this.button) {
          this.button.disabled = false;
          this.button.classList.remove('is-loading');
        }
      });
  };
}

if (!customElements.get('add-to-cart-pill')) {
  customElements.define('add-to-cart-pill', AddToCartPill);
}

/**
 * Product-page form (`.product-main__form`, or any form with
 * `data-brickline-ajax-cart`): submits over AJAX instead of
 * a full-page post to /cart, so the modal can open in place. Without JS the
 * form still posts normally.
 */
if (!window.__bricklineAjaxCartForms) {
  window.__bricklineAjaxCartForms = true;

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-brickline-ajax-cart], .product-main__form')) return;

    const submitter = /** @type {HTMLButtonElement | null} */ (
      event.submitter || form.querySelector('[type="submit"]')
    );
    if (submitter?.disabled) return;

    event.preventDefault();

    if (submitter) {
      submitter.disabled = true;
      submitter.classList.add('is-loading');
    }

    addToCart(form, new FormData(form), 'product').finally(() => {
      if (submitter) {
        submitter.disabled = false;
        submitter.classList.remove('is-loading');
      }
    });
  });
}
